// Captures a ?ref=CODE URL parameter and stores it in localStorage so
// it survives across the login flow. Once the customer is logged in,
// attachStoredReferral() POSTs it to /portal/referral/attach and clears
// the entry — silently swallowing errors (already-attached / unknown
// code / self-referral all show up as 4xx but the user shouldn't be
// blocked from using the shop).
//
// Mirrors the "?ref" capture pattern used by WHMCS-Affiliates and most
// SaaS-affiliate-platforms — the code lives client-side until we have
// a session to bind it to.

import apiClient from '../api/client'

const STORAGE_KEY = 'nc.refcode'

export function captureReferralFromURL(): void {
  try {
    const url = new URL(window.location.href)
    const ref = url.searchParams.get('ref')
    if (ref && ref.length >= 3 && ref.length <= 40 && /^[A-Za-z0-9_-]+$/.test(ref)) {
      localStorage.setItem(STORAGE_KEY, ref)
    }
  } catch {
    // ignore — localStorage may be unavailable (Safari private mode)
  }
}

export function storedReferral(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function clearStoredReferral(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch { /* ignore */ }
}

// Call this from any authenticated portal page on mount. The endpoint
// is idempotent and self-referral is blocked server-side, so we can
// fire-and-forget. We clear the code regardless of outcome — a code
// that doesn't apply (unknown / self / already-referred) shouldn't
// keep retrying on every page load.
export async function attachStoredReferral(): Promise<void> {
  const code = storedReferral()
  if (!code) return
  try {
    await apiClient.post('/api/v1/portal/referral/attach', { code })
  } catch {
    // silent — the user is in the shop, they don't need to see this
  } finally {
    clearStoredReferral()
  }
}
